import { Box, Skeleton, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { getAvailableFieldsWithTypes } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import { schemas as finleySchemas } from '@finley-technologies/finley-types';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { LoanProductEntity } from '../entity/LoanProductEntity';
import { LoanProductBaseFieldConfig } from './LoanProductBaseFieldConfig';
import { LoanProductConcentrationLimits } from './LoanProductConcentrationLimits';
import { LoanProductEligibilityTests } from './LoanProductEligibilityTests';
import { LoanProductIntermediateCalcs } from './LoanProductIntermediateCalcs';
import { LoanProductPurchasePercentageRate } from './LoanProductPurchasePercentageRate';
import { LoanProductReceivableAdvanceRate } from './LoanProductReceivableAdvanceRate';
import { LoanProductReceivableValuationRate } from './LoanProductReceivableValuationRate';
import { LoanProductVintageFormulaDenominator } from './LoanProductVintageFormulaDenominator';
import { ValleyBankAdvanceRate } from './ValleyBankLenderReceivableAdvanceRate';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useSetupMonacoEditor } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/hooks/useSetupMonacoEditor';
import { AssignedLoanTapeCsvConfigs } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/LoanProductAssignedLoanTapeCsvConfigs';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
type LoanProductProps = {
  creditFacilityId: string;
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  idx: number;
  basePropertyKey: string;
  loanProductOptions: { value: string; label: string }[];
  currentCommittedConcentrationLimitKeys: string[];
  currentCommittedEligibilityTestsKeys: string[];
  showReceivableAdvanceRateTreeConfig?: boolean;
  isValleyBankLenderView?: boolean;
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
};
export const LoanProduct = ({
  creditFacilityId,
  loanProductMechanics,
  wipConfig,
  idx,
  basePropertyKey,
  loanProductOptions,
  currentCommittedConcentrationLimitKeys,
  currentCommittedEligibilityTestsKeys,
  showReceivableAdvanceRateTreeConfig,
  isValleyBankLenderView,
  multiLoanProductConcentrationLimits,
}: LoanProductProps) => {
  const flags = useFeatureFlags();
  useSetupMonacoEditor({
    intermediateCalcs: loanProductMechanics._config_.intermediateCalcConfigs,
    baseFields: loanProductMechanics._config_.baseFieldConfigs,
    jsonSchema: finleySchemas.ConcentrationLimitSourceConfigSchema,
    flags: {
      isExcelEngineAdvanceRateEnabled: !!flags.isExcelEngineAdvanceRateEnabled,
    },
    loanTapeMetadataPortfolioCovenantKeys: wipConfig
      ? wipConfig.portfolioCovenantKeys
      : [],
  });

  const loanProductKey = `${basePropertyKey}[${idx}]`;
  if (!loanProductMechanics || !loanProductMechanics.entity) {
    return <Skeleton height="600px" />;
  }

  const availableFields = getAvailableFieldsWithTypes({
    loanProducts: [loanProductMechanics],
    loanProductIds: [loanProductMechanics.entity.id],
  });

  if (isValleyBankLenderView) {
    finleyAssert(
      loanProductMechanics.receivableAdvanceRate._config_
        .receivableAdvanceRateConfig.type === 'tree',
      'Must have a tree receivable advance rate config.',
    );
    return (
      <Box mt="20px" width="100%" p="20px" borderWidth="1px">
        <ValleyBankAdvanceRate
          receivableAdvanceRateConfig={
            loanProductMechanics.receivableAdvanceRate._config_
              .receivableAdvanceRateConfig
          }
          basePropertyKey={basePropertyKey}
          idx={idx}
          wipConfig={wipConfig}
          loanProductMechanicsConfig={loanProductMechanics._config_}
          loanProductKey={loanProductKey}
          concentrationLimitConfigs={loanProductMechanics.concentrationLimits
            .map((limit) => limit._config_.concentrationLimitConfig)
            .filter((config) => !!config)}
        />
      </Box>
    );
  }

  return (
    <Box mt="20px" width="100%" p="20px" borderWidth="1px">
      <LoanProductEntity
        loanProductEntity={loanProductMechanics.entity}
        config={loanProductMechanics._config_}
      />
      <Text fontWeight="bold" fontSize="20px" mb="10px">
        Config
      </Text>
      <LoanProductBaseFieldConfig
        creditFacilityId={creditFacilityId}
        fields={loanProductMechanics._config_.baseFieldConfigs}
        wipConfig={wipConfig}
      />
      <LoanProductIntermediateCalcs
        loanProductKey={loanProductKey}
        config={loanProductMechanics._config_}
        wipConfig={wipConfig}
        fields={loanProductMechanics._config_.baseFieldConfigs}
      />
      <LoanProductVintageFormulaDenominator
        vintageCurveFormulaDenominator={
          loanProductMechanics._config_.vintageCurveFormulaDenominator
        }
        wipConfig={wipConfig}
        loanProductKey={loanProductKey}
      />
      <Text fontWeight="bold" fontSize="20px" mb="20px">
        Properties
      </Text>
      <LoanProductReceivableAdvanceRate
        rate={loanProductMechanics.receivableAdvanceRate}
        basePropertyKey={basePropertyKey}
        idx={idx}
        wipConfig={wipConfig}
        baseFields={loanProductMechanics._config_.baseFieldConfigs}
        intermediateCalcs={
          loanProductMechanics._config_.intermediateCalcConfigs
        }
        concentrationLimitConfigs={loanProductMechanics.concentrationLimits
          .map((limit) => limit._config_.concentrationLimitConfig)
          .filter((config) => !!config)}
        // @MLP Advance Rate: can likely remove this prop
        // once we have proper support for MLP Advance Rates
        loanProductEntity={loanProductMechanics.entity}
        loanProductOptions={loanProductOptions}
        showReceivableAdvanceRateTreeConfig={
          showReceivableAdvanceRateTreeConfig
        }
      />
      <LoanProductReceivableValuationRate
        rate={loanProductMechanics.receivableValuationRate}
        basePropertyKey={basePropertyKey}
        idx={idx}
        wipConfig={wipConfig}
        baseFields={loanProductMechanics._config_.baseFieldConfigs}
        intermediateCalcs={
          loanProductMechanics._config_.intermediateCalcConfigs
        }
      />
      <LoanProductPurchasePercentageRate
        rate={loanProductMechanics.purchasePercentageRate}
        basePropertyKey={basePropertyKey}
        idx={idx}
        wipConfig={wipConfig}
        baseFields={loanProductMechanics._config_.baseFieldConfigs}
        intermediateCalcs={
          loanProductMechanics._config_.intermediateCalcConfigs
        }
      />
      <LoanProductEligibilityTests
        baseFields={loanProductMechanics._config_.baseFieldConfigs}
        intermediateCalcs={
          loanProductMechanics._config_.intermediateCalcConfigs
        }
        eligibilityTests={loanProductMechanics.eligibilityTests}
        wipConfig={wipConfig}
        idx={idx}
        basePropertyKey={basePropertyKey}
        currentCommittedEligibilityTestsKeys={
          currentCommittedEligibilityTestsKeys
        }
      />
      <LoanProductConcentrationLimits
        availableFields={availableFields}
        concentrationLimits={loanProductMechanics.concentrationLimits}
        wipConfig={wipConfig}
        idx={idx}
        basePropertyKey={basePropertyKey}
        currentCommittedConcentrationLimitKeys={
          currentCommittedConcentrationLimitKeys
        }
      />
      <AssignedLoanTapeCsvConfigs
        assignedLoanTapeCsvConfigs={
          loanProductMechanics._config_.assignedLoanTapeCsvConfigs
        }
        idx={idx}
        wipConfig={wipConfig}
        basePropertyKey={basePropertyKey}
        baseFields={loanProductMechanics._config_.baseFieldConfigs.map(
          ({ field }) => field,
        )}
        intermediateFields={loanProductMechanics._config_.intermediateCalcConfigs.map(
          ({ field }) => field,
        )}
        eligibilityPropertyKeys={_.compact(
          loanProductMechanics.eligibilityTests.map(
            ({ _config_ }) => _config_.eligibilityTestConfig?.propertyKey,
          ),
        )}
        allConcentrationLimitKeys={[
          ..._.compact(
            loanProductMechanics.concentrationLimits.map(
              ({ _config_ }) => _config_.concentrationLimitConfig?.propertyKey,
            ),
          ),
          ..._.compact(
            multiLoanProductConcentrationLimits.map(
              ({ _config_ }) => _config_.concentrationLimitConfig?.propertyKey,
            ),
          ),
        ]}
      />
    </Box>
  );
};
